import { useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import { getOrganizationId } from "modules/CommonSelectors";
import useVirtualizedBatchFetcher from "./useVirtualizedBatchFetcher";
import useBatchSearch from "./useBatchSearch";
import {
  getCourseDetailsBatchQuery,
  searchCoursesByIdsQuery,
} from "../modules/ParticipantsQueries";

const useCourseBatchDetails = ({ courseIds, searchText, skip = false }) => {
  const organizationId = getOrganizationId();

  const courseIdsSet = useMemo(() => new Set(courseIds), [courseIds]);

  // ---------------- BATCH ----------------
  const {
    onRangeChanged,
    detailsMap,
    setDetailsMap,
  } = useVirtualizedBatchFetcher({
    ids: courseIds,
    skip: skip || !!searchText,

    batchQuery: getCourseDetailsBatchQuery,

    batchVariablesBuilder: ids => ({
      ids,
    }),

    mapBatchData: data => {
      const items = _.get(data, "multiNode", []);
      return _.map(items, item => ({
        id: item.id,
        title: item.title,
        studentCount: _.get(item, "students.totalCount", 0),
        staffCount: _.get(item, "collaborators.totalCount", 0),
        familyMemberCount: _.get(item, "familyMembers.totalCount", 0),
      }));
    },
  });

  // ---------------- SEARCH ----------------
  const searchVariablesBuilder = useCallback(
    searchText => ({
      organizationId,
      searchText,
    }),
    [organizationId]
  );

  const { searchItems, searchIds, isSearchLoading } = useBatchSearch({
    ids: courseIds,
    searchText,
    skip,

    searchQuery: searchCoursesByIdsQuery,

    searchVariablesBuilder,

    mapSearchData: data => {
      const edges = _.get(data, "node.courses.edges", []);
      const nodes = _.filter(_.compact(_.map(edges, "node")), c =>
        courseIdsSet.has(c.id)
      );
      return _.map(nodes, item => ({
        id: item.id,
        title: item.title,
        studentCount: _.get(item, "students.totalCount", 0),
        staffCount: _.get(item, "collaborators.totalCount", 0),
        familyMemberCount: _.get(item, "familyMembers.totalCount", 0),
      }));
    },

    extractSearchIds: data => {
      const edges = _.get(data, "node.courses.edges", []);
      const nodes = _.filter(_.compact(_.map(edges, "node")), c =>
        courseIdsSet.has(c.id)
      );
      return _.map(nodes, "id");
    },
  });

  // Merge search into detailsMap
  useEffect(() => {
    if (!searchItems) return;

    setDetailsMap(prev => {
      const next = { ...prev };
      _.forEach(searchItems, item => {
        if (_.get(item, "id")) next[item.id] = item;
      });
      return next;
    });
  }, [searchItems, setDetailsMap]);

  return {
    onRangeChanged,
    detailsMap,
    searchResults: searchText ? searchIds : null,
    isSearchLoading,
  };
};

export default useCourseBatchDetails;
